import { useState, useMemo } from 'react';
import ChartWidget from '@/components/ChartWidget';
import type { BenchmarkData } from '@/types';

const MOCK_BENCHMARKS: BenchmarkData[] = [
  // Attrition by Industry
  { metric: 'Attrition Rate', yourValue: 12.3, benchmarkValue: 15.1, segment: 'Technology' },
  { metric: 'Attrition Rate', yourValue: 12.3, benchmarkValue: 13.8, segment: 'SaaS' },
  { metric: 'Attrition Rate', yourValue: 12.3, benchmarkValue: 17.2, segment: 'Financial Services' },
  { metric: 'Attrition Rate', yourValue: 12.3, benchmarkValue: 14.5, segment: 'Healthcare' },
  // Attrition by Company Size
  { metric: 'Attrition Rate', yourValue: 12.3, benchmarkValue: 18.3, segment: '1-100' },
  { metric: 'Attrition Rate', yourValue: 12.3, benchmarkValue: 14.7, segment: '101-500' },
  { metric: 'Attrition Rate', yourValue: 12.3, benchmarkValue: 13.2, segment: '501-2000' },
  { metric: 'Attrition Rate', yourValue: 12.3, benchmarkValue: 11.9, segment: '2000+' },
  // Compensation by Industry
  { metric: 'Avg Compensation ($K)', yourValue: 145, benchmarkValue: 152, segment: 'Technology' },
  { metric: 'Avg Compensation ($K)', yourValue: 145, benchmarkValue: 138, segment: 'SaaS' },
  { metric: 'Avg Compensation ($K)', yourValue: 145, benchmarkValue: 165, segment: 'Financial Services' },
  { metric: 'Avg Compensation ($K)', yourValue: 145, benchmarkValue: 128, segment: 'Healthcare' },
  // Compensation by Company Size
  { metric: 'Avg Compensation ($K)', yourValue: 145, benchmarkValue: 120, segment: '1-100' },
  { metric: 'Avg Compensation ($K)', yourValue: 145, benchmarkValue: 135, segment: '101-500' },
  { metric: 'Avg Compensation ($K)', yourValue: 145, benchmarkValue: 148, segment: '501-2000' },
  { metric: 'Avg Compensation ($K)', yourValue: 145, benchmarkValue: 162, segment: '2000+' },
  // Headcount Growth by Industry
  { metric: 'Headcount Growth (%)', yourValue: 8.5, benchmarkValue: 6.2, segment: 'Technology' },
  { metric: 'Headcount Growth (%)', yourValue: 8.5, benchmarkValue: 9.1, segment: 'SaaS' },
  { metric: 'Headcount Growth (%)', yourValue: 8.5, benchmarkValue: 3.8, segment: 'Financial Services' },
  { metric: 'Headcount Growth (%)', yourValue: 8.5, benchmarkValue: 5.4, segment: 'Healthcare' },
  // Headcount Growth by Company Size
  { metric: 'Headcount Growth (%)', yourValue: 8.5, benchmarkValue: 15.2, segment: '1-100' },
  { metric: 'Headcount Growth (%)', yourValue: 8.5, benchmarkValue: 10.3, segment: '101-500' },
  { metric: 'Headcount Growth (%)', yourValue: 8.5, benchmarkValue: 7.1, segment: '501-2000' },
  { metric: 'Headcount Growth (%)', yourValue: 8.5, benchmarkValue: 4.2, segment: '2000+' },
];

const METRICS = ['Attrition Rate', 'Avg Compensation ($K)', 'Headcount Growth (%)'];
const SEGMENT_TYPES = [
  { value: 'industry', label: 'Industry', segments: ['Technology', 'SaaS', 'Financial Services', 'Healthcare'] },
  { value: 'size', label: 'Company Size', segments: ['1-100', '101-500', '501-2000', '2000+'] },
];

export default function BenchmarksPage() {
  const [selectedMetric, setSelectedMetric] = useState(METRICS[0]);
  const [selectedSegmentType, setSelectedSegmentType] = useState(SEGMENT_TYPES[0]);

  const chartData = useMemo(() => {
    return MOCK_BENCHMARKS
      .filter(
        (b) =>
          b.metric === selectedMetric &&
          selectedSegmentType.segments.includes(b.segment)
      )
      .map((b) => ({
        name: b.segment,
        'Your Company': b.yourValue,
        Benchmark: b.benchmarkValue,
      }));
  }, [selectedMetric, selectedSegmentType]);

  const yourValue = chartData[0]?.['Your Company'] ?? 0;
  const avgBenchmark =
    chartData.reduce((sum, d) => sum + d.Benchmark, 0) / (chartData.length || 1);
  const diff = yourValue - avgBenchmark;
  const diffLabel = diff > 0 ? 'above' : 'below';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Industry Benchmarks
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Compare your workforce metrics against industry peers
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Metric
          </label>
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="input-field w-full sm:w-64"
          >
            {METRICS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Segment by
          </label>
          <select
            value={selectedSegmentType.value}
            onChange={(e) =>
              setSelectedSegmentType(
                SEGMENT_TYPES.find((s) => s.value === e.target.value) ||
                  SEGMENT_TYPES[0]
              )
            }
            className="input-field w-full sm:w-48"
          >
            {SEGMENT_TYPES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Card */}
      <div className="card">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
          <div>
            <p className="text-sm text-gray-500">Your {selectedMetric}</p>
            <p className="text-3xl font-bold text-gray-900">{yourValue}</p>
          </div>
          <div className="hidden sm:block h-12 w-px bg-gray-200" />
          <div>
            <p className="text-sm text-gray-500">Average Benchmark</p>
            <p className="text-3xl font-bold text-gray-900">
              {avgBenchmark.toFixed(1)}
            </p>
          </div>
          <div className="hidden sm:block h-12 w-px bg-gray-200" />
          <div>
            <p className="text-sm text-gray-500">Difference</p>
            <p
              className={`text-3xl font-bold ${
                diff > 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {Math.abs(diff).toFixed(1)} {diffLabel}
            </p>
          </div>
        </div>
      </div>

      {/* Chart */}
      <ChartWidget
        title={`${selectedMetric} -- Your Company vs ${selectedSegmentType.label} Benchmarks`}
        type="bar"
        data={chartData}
        dataKey="Your Company"
        secondaryDataKey="Benchmark"
        xKey="name"
        colors={['#2563EB', '#9CA3AF']}
        height={380}
      />
    </div>
  );
}
